import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTooltip } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { Ota } from 'frontend-utils/models';
import HotelChannelPricingTablesCellsTableCellBase from 'lumen/components/hotel/channel-pricing/tables/cells/table-cell-base';
import HotelChannelPricingTablesCellsTableCellWrapper from 'lumen/components/hotel/channel-pricing/tables/cells/table-cell-wrapper';
import type {
  TdChannelsTableColumnSignature,
  TdChannelsTableRowSignature,
} from 'lumen/components/hotel/channel-pricing/tables/channels-table';
import type { Carate, PriceAction } from 'lumen/models';
import { getLiveshopRateTooltipTranslations } from 'lumen/utils/channel-pricing/get-liveshop-rate-tooltip-translations';
import { logChannelsTablePricingCellDebugValues } from 'lumen/utils/channel-pricing/log-channels-pricing-cell-debug-values';
import {
  CHANNELS_TABLE_ROW_TYPES,
  UNSUPPORTED_CLOSING_THE_LOOP_CHANNELS,
} from 'lumen/utils/constants/channel-pricing-constants';
import { MEALTYPE_ICONS } from 'lumen/utils/constants/general-constants';
import { differenceInDays } from 'lumen/utils/dates/native-js-date-utils';

import styles from './channels-table-pricing-row-cell-content.module.css';

interface HotelChannelPricingTablesCellsChannelsTablePricesChannelsTablePricingRowCellContentSignature {
  Args: {
    column: TdChannelsTableColumnSignature;
    row: TdChannelsTableRowSignature;
  };
}

/**
 * We check the carate.comparisonRates, similar as in the PMS table.
 * In the PMS table the comparisonRates can be used to validate if the current price deviates from the expected roomtype hierarchy
 * In the Channels table we use it to check if the carate value (Channel Manager value) deviates from the PMS price + configured offset
 *
 * @param carate
 */
function checkInconsistencyInRate(carate: Carate | undefined): boolean {
  if (carate?.comparisonRates && carate.comparisonRates[0]) {
    return (
      carate.comparisonRates[0].difference_absolute !== 0 &&
      carate.comparisonRates[0].difference_relative !== 0
    );
  }

  return false;
}

export default class HotelChannelPricingTablesCellsChannelsTablePricesChannelsTablePricingRowCellContent extends Component<HotelChannelPricingTablesCellsChannelsTablePricesChannelsTablePricingRowCellContentSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('channel-pricing/liveupdate')
  declare liveupdate: Services['channel-pricing/liveupdate'];
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];

  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  /**
   * Checks if Closing The Loop features can be shown.
   * Combines feature flag, non-past day and supported channel requirements.
   */
  get canShowClosingTheLoopFeatures(): boolean {
    return (
      this.isClosingTheLoopFeatureEnabled &&
      !this.args.column.isPastDay &&
      !UNSUPPORTED_CLOSING_THE_LOOP_CHANNELS.includes(
        this.args.column.selectedChannelId,
      )
    );
  }

  get carate(): Carate | undefined {
    return this.args.column.caratesByHotelInfoId[this.args.row.hotelInfoId];
  }

  get closingTheLoopIcon(): IconName | undefined {
    if (!this.shouldShowClosingTheLoopIcon) {
      return undefined;
    }

    if (this.isLiveupdateInProgress) {
      return 'sync';
    }

    if (!this.carate?.message) {
      if (this.carate?.isRateDiscountDetected) {
        return 'discount-filled';
      }

      if (this.carate?.isRateShoppedValueAsExpected) {
        return 'circle-checkmark';
      }
    }

    return undefined;
  }

  get formattedDate(): string {
    return this.args.column.day.date.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );
  }

  get hasInconsistencyInRate(): boolean {
    if (!this.args.column.isPastDay) {
      return checkInconsistencyInRate(this.carate);
    }

    return false;
  }

  get hasRoomnameTooltip(): boolean {
    return Boolean(this.isCompetitorRow && this.roomName);
  }

  get iconClass(): string {
    // iconClass is used to show the spinning animation when a liveupdate is in progress
    // This should only happen for the own hotel base room row. Otherwise all icons in the Channels table will spin.
    if (!this.isCompetitorRow && this.isLiveupdateInProgress) {
      return 'spinning';
    }

    if (this.isOwnHotelSoldOut) {
      return styles['hotel-is-sold-out'];
    }

    return '';
  }

  get iconColor(): 'inherit' | 'info' {
    if (
      !this.isCompetitorRow &&
      this.hasInconsistencyInRate &&
      this.mainIcon === this.inconsistencyIcon // Note: only apply the info color in case the inconsistency icon is actually rendered
    ) {
      return 'info';
    }

    return 'inherit';
  }

  get inconsistencyIcon(): IconName | undefined {
    if (!this.isCompetitorRow && this.hasInconsistencyInRate) {
      return 'bullet';
    }

    return undefined;
  }

  get isClosingTheLoopFeatureEnabled(): boolean {
    return this.access.hasSmartDistributionClosingTheLoopFeature;
  }

  get isCompetitorRow(): boolean {
    return this.args.row.type === CHANNELS_TABLE_ROW_TYPES.PRICING_CHILD_ROW;
  }

  get isInPushingState(): boolean {
    return (
      !this.isCompetitorRow &&
      (this.args.column.daysWithActivePricePush.includes(this.args.column.id) ||
        this.pricingAssistantRecommendationActionPoller.datesBeingRefreshed.has(
          this.args.column.id,
        ))
    );
  }

  get isLiveupdateInProgress(): boolean {
    return (
      this.liveupdate.areTasksRunning &&
      this.liveupdate.isDateWithinActiveLiveupdateRange(
        this.args.column.day.date,
      )
    );
  }

  get isOwnHotelSoldOut(): boolean {
    return !this.isCompetitorRow && Boolean(this.carate?.isSoldOut);
  }

  /**
   * Validates if rate data is current enough for Closing The Loop analysis.
   * Returns true when rate was shopped after the price push, or when no relevant price action exists.
   */
  get isRateDataValidForClosingTheLoop(): boolean {
    if (!this.isClosingTheLoopFeatureEnabled) {
      return false;
    }
    return !this.latestPushedPriceAction || this.isRateShoppedAfterPricePush;
  }

  get isRateShoppedAfterPricePush(): boolean {
    return (
      this.carate?.extractDatetime?.isAfter(
        this.latestPushedPriceAction?.createdAt,
      ) ?? false
    );
  }

  /**
   * Returns the latest pushed price action for the parent row.
   * Returns null if:
   * - The row is not a parent row
   * - No price action exists
   * - The price action is irrelevant for closing the loop analysis
   *
   * Returning null bypasses the price action vs carate timestamp comparison,
   * allowing closing the loop icons to be shown.
   */
  get latestPushedPriceAction(): PriceAction | null {
    const isParentRow =
      this.args.row.type === CHANNELS_TABLE_ROW_TYPES.PRICING_PARENT_ROW;
    const hasPriceAction = Boolean(this.args.column.priceAction);
    const isRelevantForClosingTheLoop =
      !this.args.column.priceAction?.isIrrelevantForClosingTheLoop;

    if (!isParentRow || !hasPriceAction || !isRelevantForClosingTheLoop) {
      return null;
    }

    return this.args.column.priceAction as PriceAction;
  }

  /**
   * Returns an object with translated title and text for the liveshop price comparison tooltip.
   */
  get liveshopRateTooltipTranslations(): {
    text: string;
    title: string;
  } {
    const tooltipKeys = getLiveshopRateTooltipTranslations(
      this.carate,
      this.isRateDataValidForClosingTheLoop,
    );
    const title = tooltipKeys.title ? this.intl.t(tooltipKeys.title) : '';
    const text = this.getTooltipText(tooltipKeys);

    return { title, text };
  }

  get mainIcon(): IconName | undefined {
    return (
      this.closingTheLoopIcon || this.inconsistencyIcon || this.mealtypeIcon
    );
  }

  get mainValueClass(): string {
    if (this.isOwnHotelSoldOut) {
      return styles['hotel-is-sold-out'];
    }

    if (this.isCompetitorRow) {
      return styles['competitor-price'];
    }

    return '';
  }

  get mealtypeIcon(): IconName | undefined {
    if (!this.carate || !this.isCompetitorRow) {
      return undefined;
    }

    return MEALTYPE_ICONS[this.carate.mealtypeIncluded];
  }

  get otaRedirectLink(): string {
    return this.args.column.otaRedirectLink;
  }

  get roomName(): string {
    if (this.carate) {
      return this.carate.roomName;
    }

    return '';
  }

  /**
   * Determines if Closing The Loop icon (checkmark/discount/sync) should be displayed.
   * Requires enabled feature, non-competitor row, present rate, and either valid rate data or active liveupdate.
   */
  get shouldShowClosingTheLoopIcon(): boolean {
    return (
      this.canShowClosingTheLoopFeatures &&
      !this.isCompetitorRow &&
      Boolean(this.carate) &&
      (this.isRateDataValidForClosingTheLoop || this.isLiveupdateInProgress)
    );
  }

  get showException(): boolean {
    // Exception state (aka failed recommendation push) can only happen for the base room row of the own hotel
    return (
      !this.isCompetitorRow &&
      !this.isInPushingState &&
      this.args.column.daysWithFailedPricePush.includes(this.args.column.id)
    );
  }

  get showLiveshopPriceCompareTooltip(): boolean {
    return (
      this.canShowClosingTheLoopFeatures &&
      !this.isCompetitorRow &&
      !this.isInPushingState &&
      !this.isLiveupdateInProgress
    );
  }

  get showRedirectLink(): boolean {
    return this.canShowClosingTheLoopFeatures && !this.isCompetitorRow;
  }

  get value(): string {
    if (this.carate) {
      return this.carate.formattedRate;
    }
    return this.intl.t('general.missing');
  }

  // logic to determine the text of the Closing The Loop related cell tooltip
  // if shopped value is undefined: show no shopping info text
  // if shopped value exist: show matching, higher or lower text with actual shopped value and channel
  private getTooltipText(tooltipKeys: { text: string; title: string }): string {
    if (this.carate?.shoppedValue === undefined) {
      return this.intl.t(tooltipKeys.text);
    }

    const ota = this.store.peekRecord<Ota>(
      'ota',
      this.args.column.selectedChannelId,
    );
    return this.intl.t(tooltipKeys.text, {
      channel: ota?.name,
      rate: this.carate?.formattedShoppedValue,
      htmlSafe: true,
    });
  }

  @action
  handleCellClick(event: MouseEvent): void {
    // Add staff user only debugging info console dump. Can help with debugging Closing The Loop scenarios (CMD/Meta key + click on the cell)
    if (this.access.isStaffUser && !this.isCompetitorRow && event.metaKey) {
      event.stopPropagation();

      logChannelsTablePricingCellDebugValues(
        this.formattedDate,
        this.args.row.hotelInfoId,
        this.latestPushedPriceAction,
        this.carate!,
        this.isRateDataValidForClosingTheLoop,
      );
    }
  }

  @action
  trackChannelRateChecked(): void {
    this.usageAnalytics.track('Channel Rate Checked', {
      eventTrigger: 'channels-table',
      product: 'smart-distribution',
      channelId: this.args.column.selectedChannelId,
      isSoldOut: this.carate?.isSoldOut ?? false,
      daysOffsetFromToday: differenceInDays(
        this.args.column.day.date.toDate(),
        this.hotelAndMarketService.getLocalTime().toDate(),
      ),
    });
  }

  <template>
    <HotelChannelPricingTablesCellsTableCellWrapper
      @hasInteractions={{false}}
      @hasOpenCta={{false}}
      @hasRecommendation={{false}}
      @showException={{this.showException}}
      {{on "click" this.handleCellClick}}
    >
      {{#if this.showRedirectLink}}
        <a
          class="{{styles.underline-on-hover}}
            {{if this.isOwnHotelSoldOut styles.underline-sold-out}}
            h-full w-full truncate"
          href={{this.otaRedirectLink}}
          rel="noopener noreferrer"
          target="_blank"
          {{on "click" this.trackChannelRateChecked}}
        >
          <HotelChannelPricingTablesCellsTableCellBase
            @icon={{this.mainIcon}}
            @iconClass={{this.iconClass}}
            @iconColor={{this.iconColor}}
            @isLoading={{this.isInPushingState}}
            @isPriceCell={{true}}
            @main={{this.value}}
            @mainValueClass={{this.mainValueClass}}
            @showException={{this.showException}}
          />
        </a>
      {{else}}
        <HotelChannelPricingTablesCellsTableCellBase
          @icon={{this.mainIcon}}
          @iconClass={{this.iconClass}}
          @iconColor={{this.iconColor}}
          @isLoading={{this.isInPushingState}}
          @isPriceCell={{true}}
          @main={{this.value}}
          @mainValueClass={{this.mainValueClass}}
          @showException={{this.showException}}
        />
      {{/if}}
      {{#if this.showLiveshopPriceCompareTooltip}}
        <PrismTooltip
          @text={{this.liveshopRateTooltipTranslations.text}}
          @title={{this.liveshopRateTooltipTranslations.title}}
        />
      {{/if}}
    </HotelChannelPricingTablesCellsTableCellWrapper>
    {{#if this.hasRoomnameTooltip}}
      <EmberTooltip
        @side="right"
        @tooltipClass="base-tooltip {{styles.tooltip}}"
      >
        <div class="{{styles.header}} flex items-center justify-center p-400">
          <h4>
            {{this.formattedDate}}
          </h4>
        </div>
        <div class="pt-200 pr-400 pb-200 pl-400">
          <p class="text-200--regular">{{this.roomName}}</p>
        </div>
      </EmberTooltip>
    {{/if}}
  </template>
}
